// SPDX-License-Identifier: LGPL-2.1-or-later
// The layout: for a mouse and keyboard, or touch first (larger controls, a touch bar of what a keyboard would do,
// long presses for menus), chosen or as the device suggests (touch where the main pointer is a finger, as on a
// tablet), kept in this browser. The page carries it as `data-layout` (desktop or touch); parts that depend on it
// listen for LAYOUT_EVENT.

export type LayoutChoice = "auto" | "desktop" | "touch";
export type Layout = "desktop" | "touch";

export const LAYOUTS: readonly { readonly id: LayoutChoice; readonly name: string }[] = [
  { id: "auto", name: "As the device suits" },
  { id: "desktop", name: "Mouse and keyboard" },
  { id: "touch", name: "Touch and pen" },
];

/** Fired on the window when the layout changes (by choice or as the device's pointer changes). */
export const LAYOUT_EVENT = "formverk-layout";

const STORAGE_KEY = "formverk.layout";

function stored(): LayoutChoice {
  try {
    const v = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (v === "auto" || v === "desktop" || v === "touch") return v;
  } catch {
    // No storage: as the device suits.
  }
  return "auto";
}

let choice: LayoutChoice = stored();

const query = (q: string) => globalThis.matchMedia?.(q);

export function layoutChoice(): LayoutChoice {
  return choice;
}

/** The layout for a choice, given whether the device's main pointer is coarse (a finger). */
export function layoutFor(c: LayoutChoice, coarse: boolean): Layout {
  return c === "auto" ? (coarse ? "touch" : "desktop") : c;
}

/** The layout in effect. */
export function layout(): Layout {
  return layoutFor(choice, query("(pointer: coarse)")?.matches === true);
}

/** Sets the page's layout and tells what depends on it. */
export function applyLayout(): void {
  const root = globalThis.document?.documentElement;
  if (!root) return;
  const next = layout();
  if (root.dataset.layout === next) return;
  root.dataset.layout = next;
  globalThis.dispatchEvent?.(new Event(LAYOUT_EVENT));
}

/** Chooses a layout (and remembers it in this browser). */
export function setLayoutChoice(next: LayoutChoice): void {
  choice = next;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, next);
  } catch {
    // Kept for this session only.
  }
  applyLayout();
}

/** Applies the layout now and again whenever the device's main pointer changes (a keyboard docked or taken off). */
export function startLayout(): void {
  applyLayout();
  query("(pointer: coarse)")?.addEventListener("change", () => {
    if (choice === "auto") applyLayout();
  });
}

/** Subscribes to layout changes (for useSyncExternalStore). */
export function subscribeLayout(notify: () => void): () => void {
  globalThis.addEventListener?.(LAYOUT_EVENT, notify);
  return () => globalThis.removeEventListener?.(LAYOUT_EVENT, notify);
}
